import type { CSSProperties, ReactNode } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { funnelSteps, type FunnelStages } from '@/lib/notification-funnel';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/components/metrics/formatters';

/** A number of the list next to the funnel. */
export interface FunnelStat {
  label: string;
  /** Small line under the label, e.g. "das notificações enviadas". */
  hint: string;
  /** Usually text; a skeleton while what formats it (the currency) loads. */
  value: ReactNode;
  /** "acima/abaixo da média · {average}" (CAMP-03); absent = no label. */
  comparison?: { kind: 'above' | 'below'; average: string };
}

export interface NotificationFunnelProps {
  stages: FunnelStages;
  /** `shouldHideData`: every stage value is "—" (CAMP-03). */
  hideValues?: boolean;
  /** Title of the list ("No período", "Comparado à média…"). */
  statsTitle: string;
  stats: FunnelStat[];
  /** Phone layout (CAMP-05): shorter stages, list always below. */
  compact?: boolean;
}

/** Light → dark brand colors, with a text color that reads on each. */
const STAGE_COLORS: Array<[background: string, text: string]> = [
  ['#DCE6FE', '#0B348C'],
  ['#9DB8F5', '#0B348C'],
  ['#155DFC', '#FFFFFF'],
  ['#0B348C', '#FFFFFF'],
];
/** Fixed taper (% inset of each edge), not proportional to the values. */
const INSET = [0, 7, 14, 21, 28];
const GAP = 10;

/**
 * The push funnel (FUN-01…03), shared by the Analytics Push tab and the
 * campaign details: 4 fixed stages (Enviadas, Entregues, Cliques, Pedidos)
 * as trapezoids of decreasing width, the rate of each passage on the seam
 * between two stages (with an accessible label) and a titled list of numbers
 * beside it (from ~720px of room) or below it.
 */
export function NotificationFunnel({
  stages,
  hideValues = false,
  statsTitle,
  stats,
  compact = false,
}: NotificationFunnelProps) {
  const { LL, locale } = useI18nContext();
  // Tall enough that the rate on each seam (24px, half over each stage)
  // never covers a stage's label or value.
  const height = compact ? 66 : 70;

  const rows = [
    { label: LL.funnel.sent(), value: stages.sent },
    { label: LL.funnel.delivered(), value: stages.delivered },
    { label: LL.funnel.clicks(), value: stages.clicks },
    { label: LL.funnel.orders(), value: stages.orders },
  ];
  const rateText = (rate: number | null) =>
    rate === null || hideValues
      ? '—'
      : `${new Intl.NumberFormat(locale, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(rate)}%`;
  const describeStep = [
    LL.funnel.rateDelivered,
    LL.funnel.rateClicked,
    LL.funnel.rateOrdered,
  ];
  const steps = funnelSteps(stages).map((step, i) => {
    const rate = rateText(step.rate);
    return { rate, label: describeStep[i]({ rate }) };
  });

  const stageStyle = (i: number): CSSProperties => ({
    height,
    backgroundColor: STAGE_COLORS[i][0],
    color: STAGE_COLORS[i][1],
    clipPath: `polygon(${INSET[i]}% 0, ${100 - INSET[i]}% 0, ${
      100 - INSET[i + 1]
    }% 100%, ${INSET[i + 1]}% 100%)`,
  });

  return (
    <div
      data-notification-funnel
      className={cn(
        'flex gap-x-12 gap-y-6',
        compact ? 'flex-col' : 'flex-wrap items-center'
      )}
    >
      <div
        className={cn(
          'relative min-w-0',
          compact ? 'w-full' : 'max-w-[600px] flex-[1_1_400px]'
        )}
      >
        <ol
          aria-label={LL.funnel.stagesLabel()}
          className="flex flex-col"
          style={{ gap: GAP }}
        >
          {rows.map((row, i) => (
            <li
              key={row.label}
              data-funnel-stage
              className="flex flex-col items-center justify-center gap-px"
              style={stageStyle(i)}
            >
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] opacity-90">
                {row.label}
              </span>
              <span
                data-funnel-value
                className={cn(
                  'font-semibold tabular-nums tracking-[-0.02em]',
                  compact ? 'text-lg' : 'text-xl'
                )}
              >
                {hideValues ? '—' : formatNumber(row.value, locale)}
              </span>
            </li>
          ))}
        </ol>
        {steps.map((step, i) => (
          <span
            key={step.label}
            role="img"
            aria-label={step.label}
            data-funnel-rate
            className="absolute left-1/2 z-[2] inline-flex h-6 -translate-x-1/2 -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded bg-background px-2 text-xs font-semibold tabular-nums shadow-[0_0_0_1px_rgba(21,25,30,0.14),0_2px_6px_rgba(15,23,42,0.10)]"
            style={{ top: (i + 1) * (height + GAP) - GAP / 2 }}
          >
            <ArrowDown aria-hidden="true" className="size-3" />
            {step.rate}
          </span>
        ))}
      </div>

      <div
        className={cn(
          'flex min-w-0 flex-col',
          compact ? 'w-full' : 'flex-[1_1_280px]'
        )}
      >
        <h4 className="pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {statsTitle}
        </h4>
        <dl className="flex flex-col divide-y">
          {stats.map((stat) => (
            <div
              key={stat.label}
              data-funnel-stat
              className="flex items-start justify-between gap-4 py-3"
            >
              <dt className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-semibold">{stat.label}</span>
                <span className="text-xs text-muted-foreground">
                  {stat.hint}
                </span>
              </dt>
              <dd className="flex shrink-0 flex-col items-end gap-1">
                <span className="text-lg font-semibold tabular-nums tracking-[-0.01em]">
                  {stat.value}
                </span>
                {stat.comparison ? (
                  <FunnelComparison {...stat.comparison} />
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

/** CAMP-03: green with an up arrow when ≥ average, gray with a down one. */
function FunnelComparison({
  kind,
  average,
}: {
  kind: 'above' | 'below';
  average: string;
}) {
  const { LL } = useI18nContext();
  const above = kind === 'above';
  const Arrow = above ? ArrowUp : ArrowDown;
  return (
    <span
      data-funnel-comparison={kind}
      className={cn(
        'inline-flex h-5 items-center gap-1 whitespace-nowrap rounded px-1.5 text-[11px] font-semibold',
        !above && 'bg-muted text-muted-foreground'
      )}
      style={
        above
          ? { color: '#0A7A45', backgroundColor: 'rgba(10, 122, 69, 0.09)' }
          : undefined
      }
    >
      <Arrow aria-hidden="true" className="size-3" />
      {above
        ? LL.funnel.aboveAverage({ average })
        : LL.funnel.belowAverage({ average })}
    </span>
  );
}
